import React, { useCallback, useEffect, useRef } from "react";
import { Loader } from "@googlemaps/js-api-loader";
import AsyncSelect from "react-select/async";

let placesLibraryPromise;
let placesLibraryApiKey;
let autocompleteService;

const loadPlacesLibrary = (apiKey) => {
  if (!apiKey) {
    return Promise.reject(new Error("Google Maps API key is missing."));
  }

  if (!placesLibraryPromise || placesLibraryApiKey !== apiKey) {
    placesLibraryApiKey = apiKey;
    placesLibraryPromise = new Loader({
      apiKey,
      version: "weekly",
    }).importLibrary("places");
  }

  return placesLibraryPromise;
};

const getAutocompleteService = async (apiKey) => {
  await loadPlacesLibrary(apiKey);

  if (!autocompleteService) {
    autocompleteService = new window.google.maps.places.AutocompleteService();
  }

  return autocompleteService;
};

const predictionToOption = (prediction) => ({
  label: prediction.description,
  value: prediction,
});

export default function GooglePlacesAutocomplete({
  apiKey,
  autocompletionRequest = {},
  debounce = 300,
  minLengthAutocomplete = 0,
  onLoadFailed = console.error,
  selectProps = {},
}) {
  const timeoutRef = useRef(null);
  const requestIdRef = useRef(0);

  useEffect(() => {
    return () => {
      clearTimeout(timeoutRef.current);
    };
  }, []);

  const loadOptions = useCallback(
    (inputValue, callback) => {
      const input = String(inputValue || "");
      const requestId = requestIdRef.current + 1;
      requestIdRef.current = requestId;

      clearTimeout(timeoutRef.current);

      if (input.length < minLengthAutocomplete) {
        callback([]);
        return;
      }

      timeoutRef.current = setTimeout(async () => {
        try {
          const service = await getAutocompleteService(apiKey);

          service.getPlacePredictions(
            {
              ...autocompletionRequest,
              input,
            },
            (predictions) => {
              if (requestId !== requestIdRef.current) {
                return;
              }

              callback((predictions || []).map(predictionToOption));
            }
          );
        } catch (error) {
          onLoadFailed(error);
          callback([]);
        }
      }, debounce);
    },
    [apiKey, autocompletionRequest, debounce, minLengthAutocomplete, onLoadFailed]
  );

  return (
    <AsyncSelect
      cacheOptions
      getOptionValue={(option) => option.value.place_id || option.label}
      loadOptions={loadOptions}
      {...selectProps}
    />
  );
}
